import * as THREE from 'three';

/**
 * Puppy CAD 内核 `IntCurve_IConicTool` 的二维直线、圆和椭圆等价工具。
 *
 * 职责：提供隐式函数、梯度、参数反求、D0/D1/D2 和真实/周期 domain。
 */
export class ImplicitConicTool2D {
    /** 完整圆周周期。 */
    private static readonly TWO_PI = Math.PI * 2;

    /** 隐式二次曲线定义。 */
    private readonly definition: ImplicitConicDefinition2D;

    /**
     * 创建 Puppy CAD 内核 等价隐式二次曲线工具。
     * @param definition 直线、圆或椭圆定义。
     */
    public constructor(definition: ImplicitConicDefinition2D) {
        this.definition = definition;
    }

    /** 获取隐式二次曲线类型。 */
    public get kind(): ImplicitConicDefinition2D['kind'] {
        return this.definition.kind;
    }

    /**
     * 校验隐式曲线定义。
     * @returns 定义有效时返回 true。
     */
    public isValid(): boolean {
        if (this.definition.kind === 'line') {
            return this.isFinitePoint(this.definition.start)
                && this.isFinitePoint(this.definition.end)
                && this.definition.start.distanceToSquared(this.definition.end) > Number.EPSILON;
        }
        if (this.definition.kind === 'circle') {
            return this.isFinitePoint(this.definition.center)
                && Number.isFinite(this.definition.radius)
                && this.definition.radius > 0;
        }
        return this.isFinitePoint(this.definition.center)
            && Number.isFinite(this.definition.majorRadius)
            && Number.isFinite(this.definition.minorRadius)
            && this.definition.majorRadius >= this.definition.minorRadius
            && this.definition.minorRadius > 0
            && Number.isFinite(this.definition.rotation);
    }

    /**
     * 计算 Puppy CAD 内核 隐式函数值。
     * @param point 二维点。
     * @returns 零集合位于二次曲线上的函数值。
     */
    public value(point: THREE.Vector2): number {
        if (this.definition.kind === 'line') {
            const direction = this.definition.end.clone().sub(this.definition.start).normalize();
            const offset = point.clone().sub(this.definition.start);
            return direction.x * offset.y - direction.y * offset.x;
        }
        if (this.definition.kind === 'circle') {
            return point.distanceTo(this.definition.center) - this.definition.radius;
        }
        const local = this.toEllipseLocal(point);
        const scaledY = local.y * this.definition.majorRadius / this.definition.minorRadius;
        return Math.hypot(local.x, scaledY) - this.definition.majorRadius;
    }

    /**
     * 计算隐式函数世界梯度。
     * @param point 二维点。
     * @returns 世界二维梯度。
     */
    public gradient(point: THREE.Vector2): THREE.Vector2 {
        if (this.definition.kind === 'line') {
            const direction = this.definition.end.clone().sub(this.definition.start).normalize();
            return new THREE.Vector2(-direction.y, direction.x);
        }
        if (this.definition.kind === 'circle') {
            const radial = point.clone().sub(this.definition.center);
            const length = radial.length();
            return length <= Number.EPSILON ? new THREE.Vector2() : radial.divideScalar(length);
        }
        const local = this.toEllipseLocal(point);
        const axisScale = this.definition.majorRadius / this.definition.minorRadius;
        const denominator = Math.hypot(local.x, local.y * axisScale);
        if (denominator <= Number.EPSILON) return new THREE.Vector2();
        return this.rotateEllipseVectorToWorld(new THREE.Vector2(
            local.x / denominator,
            axisScale * axisScale * local.y / denominator,
        ));
    }

    /**
     * 根据曲线上的点反求隐式曲线参数。
     * @param point 曲线附近二维点。
     * @returns 直线原生参数或 `[0,2π)` 二次曲线角参数。
     */
    public parameter(point: THREE.Vector2): number {
        if (this.definition.kind === 'line') {
            const direction = this.definition.end.clone().sub(this.definition.start);
            return point.clone().sub(this.definition.start).dot(direction) / direction.lengthSq();
        }
        if (this.definition.kind === 'circle') {
            return this.normalizeAngle(Math.atan2(
                point.y - this.definition.center.y,
                point.x - this.definition.center.x,
            ));
        }
        const local = this.toEllipseLocal(point);
        return this.normalizeAngle(Math.atan2(
            local.y / this.definition.minorRadius,
            local.x / this.definition.majorRadius,
        ));
    }

    /**
     * 按隐式曲线参数计算曲线点。
     * @param parameter 直线原生参数或二次曲线角参数。
     * @returns 二维曲线点。
     */
    public pointAt(parameter: number): THREE.Vector2 {
        if (this.definition.kind === 'line') {
            return this.definition.start.clone().lerp(this.definition.end, parameter);
        }
        if (this.definition.kind === 'circle') {
            return new THREE.Vector2(
                this.definition.center.x + this.definition.radius * Math.cos(parameter),
                this.definition.center.y + this.definition.radius * Math.sin(parameter),
            );
        }
        return this.definition.center.clone().add(this.rotateEllipseVectorToWorld(new THREE.Vector2(
            this.definition.majorRadius * Math.cos(parameter),
            this.definition.minorRadius * Math.sin(parameter),
        )));
    }

    /**
     * 计算隐式曲线一阶导数。
     * @param parameter 曲线参数。
     * @returns 一阶导数。
     */
    public derivative(parameter: number): THREE.Vector2 {
        if (this.definition.kind === 'line') {
            return this.definition.end.clone().sub(this.definition.start);
        }
        if (this.definition.kind === 'circle') {
            return new THREE.Vector2(
                -this.definition.radius * Math.sin(parameter),
                this.definition.radius * Math.cos(parameter),
            );
        }
        return this.rotateEllipseVectorToWorld(new THREE.Vector2(
            -this.definition.majorRadius * Math.sin(parameter),
            this.definition.minorRadius * Math.cos(parameter),
        ));
    }

    /**
     * 计算隐式曲线二阶导数。
     * @param parameter 曲线参数。
     * @returns 二阶导数。
     */
    public secondDerivative(parameter: number): THREE.Vector2 {
        if (this.definition.kind === 'line') return new THREE.Vector2();
        if (this.definition.kind === 'circle') {
            return new THREE.Vector2(
                -this.definition.radius * Math.cos(parameter),
                -this.definition.radius * Math.sin(parameter),
            );
        }
        return this.rotateEllipseVectorToWorld(new THREE.Vector2(
            -this.definition.majorRadius * Math.cos(parameter),
            -this.definition.minorRadius * Math.sin(parameter),
        ));
    }

    /**
     * 创建 Puppy CAD 内核 等价隐式曲线 domain。
     * @param endpointTolerance 端点世界空间容差。
     * @returns 有界、无限或周期参数域。
     */
    public createDomain(endpointTolerance: number): ParametricIntersectionDomain2D {
        if (this.definition.kind === 'line' && !this.definition.bounded) {
            return {
                hasFirstPoint: false,
                hasLastPoint: false,
                firstPoint: null,
                lastPoint: null,
                firstParameter: null,
                lastParameter: null,
                firstTolerance: endpointTolerance,
                lastTolerance: endpointTolerance,
                closed: false,
                equivalentFirstParameter: null,
                equivalentLastParameter: null,
            };
        }
        const firstParameter = 0;
        const lastParameter = this.definition.kind === 'line' ? 1 : ImplicitConicTool2D.TWO_PI;
        return {
            hasFirstPoint: true,
            hasLastPoint: true,
            firstPoint: this.pointAt(firstParameter),
            lastPoint: this.pointAt(lastParameter),
            firstParameter,
            lastParameter,
            firstTolerance: endpointTolerance,
            lastTolerance: endpointTolerance,
            closed: this.definition.kind !== 'line',
            equivalentFirstParameter: this.definition.kind === 'line' ? null : 0,
            equivalentLastParameter: this.definition.kind === 'line' ? null : ImplicitConicTool2D.TWO_PI,
        };
    }

    /**
     * 将参数规范到与有界周期 domain 相容的展开参数。
     * @param parameter 原始参数。
     * @param domain 隐式曲线 domain。
     * @returns Puppy CAD 内核 `NormalizeOnDomain` 等价参数。
     */
    public normalizeOnDomain(parameter: number, domain: ParametricIntersectionDomain2D): number {
        if (
            !domain.closed
            || domain.firstParameter === null
            || domain.lastParameter === null
            || domain.equivalentFirstParameter === null
            || domain.equivalentLastParameter === null
        ) return parameter;
        const period = domain.equivalentLastParameter - domain.equivalentFirstParameter;
        let result = parameter;
        while (result < domain.firstParameter && result + period < domain.lastParameter) result += period;
        while (result > domain.lastParameter && result - period > domain.firstParameter) result -= period;
        return result;
    }

    /**
     * 将世界点变换到椭圆局部轴系。
     * @param point 世界二维点。
     * @returns 椭圆局部点。
     */
    private toEllipseLocal(point: THREE.Vector2): THREE.Vector2 {
        if (this.definition.kind !== 'ellipse') return point.clone();
        const offset = point.clone().sub(this.definition.center);
        const cosine = Math.cos(this.definition.rotation);
        const sine = Math.sin(this.definition.rotation);
        return new THREE.Vector2(
            cosine * offset.x + sine * offset.y,
            -sine * offset.x + cosine * offset.y,
        );
    }

    /**
     * 将椭圆局部向量旋转回世界坐标。
     * @param vector 椭圆局部向量。
     * @returns 世界二维向量。
     */
    private rotateEllipseVectorToWorld(vector: THREE.Vector2): THREE.Vector2 {
        if (this.definition.kind !== 'ellipse') return vector.clone();
        const cosine = Math.cos(this.definition.rotation);
        const sine = Math.sin(this.definition.rotation);
        return new THREE.Vector2(
            cosine * vector.x - sine * vector.y,
            sine * vector.x + cosine * vector.y,
        );
    }

    /** 将角度规范到 `[0,2π)`。 */
    private normalizeAngle(angle: number): number {
        return ((angle % ImplicitConicTool2D.TWO_PI) + ImplicitConicTool2D.TWO_PI)
            % ImplicitConicTool2D.TWO_PI;
    }

    /** 判断二维点坐标是否有限。 */
    private isFinitePoint(point: THREE.Vector2): boolean {
        return Number.isFinite(point.x) && Number.isFinite(point.y);
    }
}
